/**
 * Calculate dynamic widths for timeline views when content doesn't fill available space
 * @param {string} viewType - The timeline view type (year, quarter, month, week, day)
 * @param {number} availableWidth - Available container width in pixels
 * @param {object} displayYearRange - Object with start and end year
 * @param {number} sidebarWidth - Width of the sidebar in pixels
 * @returns {object} - Object containing widths and whether to use dynamic sizing
 */
export const calculateDynamicWidths = (viewType, availableWidth, displayYearRange, sidebarWidth) => {
  const MIN_YEAR_WIDTH = 501; // Minimum width per year (4 quarters * 125.25)
  const MIN_QUARTER_WIDTH = 125.25; // Minimum width per quarter
  
  // Calculate container width (viewport - sidebar)
  const containerWidth = availableWidth - sidebarWidth;
  
  // Calculate number of years in range
  const numberOfYears = displayYearRange.end - displayYearRange.start + 1;
  
  // Default fixed widths
  const fixedWidths = {
    year: MIN_YEAR_WIDTH,
    quarter: MIN_QUARTER_WIDTH,
  };
  
  // Calculate total fixed width needed
  const totalFixedWidth = numberOfYears * MIN_YEAR_WIDTH;
  
  // Determine if we should use dynamic widths
  const shouldUseDynamic = totalFixedWidth < containerWidth;
  
  if (!shouldUseDynamic) {
    return {
      useDynamic: false,
      yearWidth: fixedWidths.year,
      quarterWidth: fixedWidths.quarter,
      totalWidth: totalFixedWidth,
    };
  }
  
  // Calculate dynamic widths to fill available space
  const dynamicYearWidth = Math.floor(containerWidth / numberOfYears);
  const dynamicQuarterWidth = dynamicYearWidth / 4;
  
  return {
    useDynamic: true,
    yearWidth: dynamicYearWidth,
    quarterWidth: dynamicQuarterWidth,
    totalWidth: dynamicYearWidth * numberOfYears,
  };
};

/**
 * Get the appropriate width for a view type
 * @param {string} viewType - The timeline view type
 * @param {object} dynamicWidths - The dynamic widths object from calculateDynamicWidths
 * @returns {number} - The width to use for the view
 */
export const getViewWidth = (viewType, dynamicWidths) => {
  if (!dynamicWidths.useDynamic) {
    // Return fixed widths
    const fixedWidths = {
      month: 241,
      quarter: 501,
      year: 501,
      week: 241,
      day: 241,
    };
    return fixedWidths[viewType] || 241;
  }
  
  // Return dynamic widths for supported views
  switch (viewType.toLowerCase()) {
    case 'year':
      return dynamicWidths.yearWidth;
    case 'quarter':
      return dynamicWidths.quarterWidth;
    default:
      return dynamicWidths[`${viewType}Width`] || 241;
  }
};
